import { useState } from "react";
import { useUser } from "./api/userHooks";
import { useAuthStore } from "@/lib/store/authStore";
import { UserProfileEditModal } from "./UserProfileEditModal";
import { ChangePasswordModal } from "./ChangePasswordModal";

export function UserProfilePage() {
  const username = useAuthStore((state) => state.username);
  const { data: userData, isLoading, error } = useUser(username || "");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSection, setEditSection] = useState<"basic" | "more">("basic");
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  const handleSectionClick = (section: "basic" | "more") => {
    setEditSection(section);
    setEditModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading profile...</div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Error loading profile</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">User Profile</h2>
          <p className="text-muted-foreground">
            View your profile information
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
            Basic Information
          </h3>
          <div
            className="cursor-pointer rounded-2xl overflow-hidden bg-card"
            onClick={() => handleSectionClick("basic")}
          >
            <div className="space-y-0">
              <div className="flex items-center justify-between px-4 pt-5 pb-4">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm">{userData.email || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Full Name</span>
                <span className="text-sm">{userData.full_name || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">First Name</span>
                <span className="text-sm">{userData.first_name || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Middle Name</span>
                <span className="text-sm">{(userData as any).middle_name || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Last Name</span>
                <span className="text-sm">{userData.last_name || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">User Type</span>
                <span className="text-sm">{userData.user_type || "System User"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5 pb-5">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="text-sm">{userData.enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
            More Information
          </h3>
          <div
            className="cursor-pointer rounded-2xl overflow-hidden bg-card"
            onClick={() => handleSectionClick("more")}
          >
            <div className="space-y-0">
              <div className="flex items-center justify-between px-4 pt-5 pb-4">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span className="text-sm">{userData.phone || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Mobile Number</span>
                <span className="text-sm">{userData.mobile_no || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Gender</span>
                <span className="text-sm">{(userData as any).gender || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Birth Date</span>
                <span className="text-sm">{(userData as any).birth_date || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="text-sm">{(userData as any).location || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className="flex items-center justify-between px-4 py-5">
                <span className="text-sm text-muted-foreground">Language</span>
                <span className="text-sm">{userData.language || "-"}</span>
              </div>
              <div className="mx-4 border-t border-border/40" />
              <div className={`flex items-center justify-between px-4 py-5 ${!(userData as any).bio && !(userData as any).interest ? 'pb-5' : ''}`}>
                <span className="text-sm text-muted-foreground">Time Zone</span>
                <span className="text-sm">{userData.time_zone || "-"}</span>
              </div>
  // @ts-expect-error - Property may not exist on type
              {(userData as any).bio && (
                <>
                  <div className="mx-4 border-t border-border/40" />
                  <div className={`flex items-start justify-between px-4 py-5 ${!(userData as any).interest ? 'pb-5' : ''}`}>
                    <span className="text-sm text-muted-foreground">Bio</span>
                    <span className="text-sm text-right max-w-[60%] whitespace-pre-wrap">{(userData as any).bio || ""}</span>
                  </div>
                </>
              )}
              // @ts-expect-error - Property may not exist on type
              {(userData as any).interest && (
                <>
                  <div className="mx-4 border-t border-border/40" />
                  <div className="flex items-start justify-between px-4 py-5 pb-5">
                    <span className="text-sm text-muted-foreground">Interests</span>
                    <span className="text-sm text-right max-w-[60%] whitespace-pre-wrap">{(userData as any).interest || ""}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
            Security
          </h3>
          <div
            className="cursor-pointer rounded-2xl overflow-hidden bg-card"
            onClick={() => setChangePasswordModalOpen(true)}
          >
            <div className="flex items-center justify-between px-4 pt-5 pb-5 min-h-[60px]">
              <div>
                <span className="text-sm text-muted-foreground">Change Password</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Set New Password
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserProfileEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        section={editSection}
      />

      <ChangePasswordModal
        open={changePasswordModalOpen}
        onOpenChange={setChangePasswordModalOpen}
      />
    </>
  );
}

